import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchHomepageSettings,
  fetchStoreSettings,
  fetchBrands,
  saveCollectionHeaders,
  saveHomepageSettings,
  saveStoreSettings,
  uploadProductImage,
} from '../lib/adminApi';
import HeaderCropEditor from '../components/HeaderCropEditor';
import {
  brandHeaderKey,
  cropMediaStyle,
  DEFAULT_CROP,
  getCollectionHeader,
  isSportHeaderKey,
  leagueHeaderKey,
  normalizeCollectionHeaders,
  SHOP_HEADER_KEY,
  sportHeaderKey,
  teamHeaderKey,
} from '../../lib/collectionHeaders';
import { cssUrl } from '../../utils/imageUrl';
import { SPORTS, teamToSlug } from '../../utils/sports';

function buildHeaderTree() {
  return SPORTS.map(sport => ({
    key: sportHeaderKey(sport.slug),
    label: sport.name,
    hint: `General ${sport.name} landing`,
    leagues: (sport.leagues || []).map(league => ({
      key: leagueHeaderKey(sport.slug, league.slug),
      label: league.name,
      hint: `All ${league.name} teams`,
      teams: (league.teams || []).map(team => ({
        key: teamHeaderKey(sport.slug, league.slug, teamToSlug(team)),
        label: team,
        hint: `${league.name} · ${team}`,
      })),
    })),
    teams: (sport.teams || []).map(team => ({
      key: teamHeaderKey(sport.slug, '', teamToSlug(team)),
      label: team,
      hint: `${sport.name} · ${team}`,
    })),
  }));
}

function slotLabelForKey(key, headerTree, licenseBrands) {
  if (key === SHOP_HEADER_KEY) return 'Shop — all products';
  for (const sport of headerTree) {
    if (sport.key === key) return sport.label;
    for (const league of sport.leagues || []) {
      if (league.key === key) return league.label;
      for (const team of league.teams || []) {
        if (team.key === key) return team.label;
      }
    }
    for (const team of sport.teams || []) {
      if (team.key === key) return team.label;
    }
  }
  const brand = (licenseBrands || []).find(item => brandHeaderKey(item.slug) === key);
  return brand?.name || 'Header';
}

function HeaderSlotRow({
  slot,
  entry,
  uploading,
  disabled,
  indent = 0,
  onUpload,
  onAdjust,
  onRemove,
}) {
  const imageUrl = entry?.image_url || '';
  const previewCrop = isSportHeaderKey(slot.key) ? entry?.home_crop : entry?.header_crop;

  return (
    <div
      className="admin-hero-slide-row"
      style={indent ? { marginLeft: indent * 16 } : undefined}
    >
      {imageUrl ? (
        <div
          className="admin-header-slot-preview"
          style={{
            backgroundImage: cssUrl(imageUrl),
            ...cropMediaStyle(previewCrop),
          }}
          role="img"
          aria-label={`${slot.label} header`}
        />
      ) : (
        <div className="admin-header-slot-placeholder" aria-hidden="true" />
      )}
      <div className="admin-hero-slide-meta">
        <strong>{slot.label}</strong>
        <span>{slot.hint}</span>
      </div>
      <div className="admin-hero-slide-actions">
        <button
          type="button"
          className="admin-btn admin-btn-secondary admin-btn-sm"
          disabled={disabled}
          onClick={() => onUpload(slot.key)}
        >
          {uploading ? 'Uploading…' : imageUrl ? 'Replace' : 'Upload'}
        </button>
        {imageUrl ? (
          <button
            type="button"
            className="admin-btn admin-btn-secondary admin-btn-sm"
            disabled={disabled}
            onClick={() => onAdjust(slot)}
          >
            Adjust
          </button>
        ) : null}
        {imageUrl ? (
          <button
            type="button"
            className="admin-btn admin-btn-danger admin-btn-sm"
            disabled={disabled}
            onClick={() => onRemove(slot.key)}
          >
            Remove
          </button>
        ) : null}
      </div>
    </div>
  );
}

function Settings() {
  const heroInputRef = useRef(null);
  const collectionInputRef = useRef(null);
  const collectionUploadKeyRef = useRef('');
  const headerTree = useMemo(() => buildHeaderTree(), []);
  const [expandedSports, setExpandedSports] = useState({});
  const [expandedLeagues, setExpandedLeagues] = useState({});
  const [store, setStore] = useState({
    store_name: 'Laurenti Apparel',
    store_email: '',
    store_phone: '',
    store_address: '',
    currency: 'USD',
    currency_symbol: '$',
    shipping_flat_rate: 0,
    shipping_free_threshold: '',
    tax_rate: 0,
    tax_included: false,
    payment_methods: ['card', 'cash'],
  });
  const [homepage, setHomepage] = useState({
    hero_image_url: '',
    hero_title: '',
    hero_subtitle: '',
    hero_images: [],
    collection_headers: {},
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingCollectionKey, setUploadingCollectionKey] = useState('');
  const [heroDragging, setHeroDragging] = useState(false);
  const [licenseBrands, setLicenseBrands] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cropEditor, setCropEditor] = useState(null);
  const [savingCrop, setSavingCrop] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [storeData, homeData, brandRows] = await Promise.all([
          fetchStoreSettings(),
          fetchHomepageSettings(),
          fetchBrands(),
        ]);
        if (cancelled) return;
        if (storeData) {
          setStore(current => ({
            ...current,
            ...storeData,
            shipping_free_threshold: storeData.shipping_free_threshold ?? '',
            payment_methods: storeData.payment_methods || ['card', 'cash'],
          }));
        }
        if (homeData) {
          let slides = Array.isArray(homeData.hero_images) ? homeData.hero_images : [];
          if (!slides.length && homeData.hero_image_url) {
            slides = [{ image_url: homeData.hero_image_url, sort_order: 0 }];
          }
          setHomepage(current => ({
            ...current,
            ...homeData,
            hero_images: slides,
            collection_headers: normalizeCollectionHeaders(homeData.collection_headers),
          }));
        }
        setLicenseBrands(brandRows || []);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Could not load settings.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const setStoreField = (key, value) => {
    setStore(current => ({ ...current, [key]: value }));
  };

  const persistHomepage = async nextHomepage => {
    await saveHomepageSettings({
      hero_image_url: nextHomepage.hero_images?.[0]?.image_url || nextHomepage.hero_image_url || null,
      hero_images: nextHomepage.hero_images || [],
      hero_title: nextHomepage.hero_title || null,
      hero_subtitle: nextHomepage.hero_subtitle || null,
      collection_headers: normalizeCollectionHeaders(nextHomepage.collection_headers),
    });
  };

  const handleSave = async event => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await Promise.all([
        saveStoreSettings({
          ...store,
          shipping_flat_rate: Number(store.shipping_flat_rate) || 0,
          shipping_free_threshold:
            store.shipping_free_threshold === ''
              ? null
              : Number(store.shipping_free_threshold),
          tax_rate: Number(store.tax_rate) || 0,
          payment_methods: Array.isArray(store.payment_methods)
            ? store.payment_methods
            : String(store.payment_methods)
                .split(',')
                .map(m => m.trim())
                .filter(Boolean),
        }),
        persistHomepage(homepage),
      ]);
      setSuccess('Settings saved.');
    } catch (err) {
      setError(err.message || 'Could not save settings.');
    } finally {
      setSaving(false);
    }
  };

  const uploadHero = async fileList => {
    const files = Array.from(fileList || []).filter(file => file.type.startsWith('image/'));
    if (!files.length) return;

    setUploadingHero(true);
    setError('');
    setSuccess('');

    try {
      const uploaded = [];
      for (const file of files) {
        const url = await uploadProductImage(file, 'homepage');
        uploaded.push({ image_url: url });
      }

      const existing = homepage.hero_images || [];
      const nextSlides = [
        ...existing,
        ...uploaded.map((item, index) => ({
          ...item,
          sort_order: existing.length + index,
        })),
      ];
      const nextHomepage = {
        ...homepage,
        hero_images: nextSlides,
        hero_image_url: nextSlides[0]?.image_url || homepage.hero_image_url,
      };

      setHomepage(nextHomepage);
      await persistHomepage(nextHomepage);
      setSuccess(
        `${uploaded.length} hero image${uploaded.length === 1 ? '' : 's'} uploaded and live on the homepage.`
      );
    } catch (err) {
      setError(err.message || 'Could not upload hero image.');
    } finally {
      setUploadingHero(false);
      if (heroInputRef.current) heroInputRef.current.value = '';
    }
  };

  const uploadCollectionHeader = async (key, fileList) => {
    const file = Array.from(fileList || []).find(item => item.type.startsWith('image/'));
    if (!key || !file) return;

    setUploadingCollectionKey(key);
    setError('');
    setSuccess('');

    const previousHeaders = normalizeCollectionHeaders(homepage.collection_headers);

    try {
      const url = await uploadProductImage(file, 'homepage');
      const nextHeaders = {
        ...previousHeaders,
        [key]: {
          image_url: url,
          header_crop: { ...DEFAULT_CROP },
          home_crop: { ...DEFAULT_CROP },
        },
      };

      setHomepage(current => ({
        ...current,
        collection_headers: nextHeaders,
      }));

      const saved = await saveCollectionHeaders(nextHeaders);
      setHomepage(current => ({
        ...current,
        collection_headers: saved,
      }));

      const savedEntry = getCollectionHeader(saved, key);
      if (!savedEntry?.image_url) {
        throw new Error('Header upload finished, but the URL was not stored. Try again.');
      }
      setSuccess('Header uploaded and live on the storefront. Adjust the crop if you need to.');
      setCropEditor({
        key,
        title: slotLabelForKey(key, headerTree, licenseBrands),
        imageUrl: savedEntry.image_url,
        header_crop: savedEntry.header_crop,
        home_crop: savedEntry.home_crop,
      });
    } catch (err) {
      setHomepage(current => ({
        ...current,
        collection_headers: previousHeaders,
      }));
      setError(err.message || 'Could not upload collection header.');
    } finally {
      setUploadingCollectionKey('');
      collectionUploadKeyRef.current = '';
      if (collectionInputRef.current) collectionInputRef.current.value = '';
    }
  };

  const removeCollectionHeader = async key => {
    setError('');
    setSuccess('');
    const previousHeaders = normalizeCollectionHeaders(homepage.collection_headers);
    const { [key]: _removed, ...rest } = previousHeaders;

    setHomepage(current => ({
      ...current,
      collection_headers: rest,
    }));

    try {
      const saved = await saveCollectionHeaders(rest);
      setHomepage(current => ({
        ...current,
        collection_headers: saved,
      }));
      setSuccess('Collection header removed.');
    } catch (err) {
      setHomepage(current => ({
        ...current,
        collection_headers: previousHeaders,
      }));
      setError(err.message || 'Could not remove collection header.');
    }
  };

  const removeHeroSlide = async index => {
    setError('');
    setSuccess('');

    const nextSlides = (homepage.hero_images || [])
      .filter((_, i) => i !== index)
      .map((item, i) => ({ ...item, sort_order: i }));
    const nextHomepage = {
      ...homepage,
      hero_images: nextSlides,
      hero_image_url: nextSlides[0]?.image_url || '',
    };

    setHomepage(nextHomepage);

    try {
      await persistHomepage(nextHomepage);
      setSuccess('Hero slideshow updated.');
    } catch (err) {
      setError(err.message || 'Could not update hero slideshow.');
    }
  };

  const moveHeroSlide = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= (homepage.hero_images || []).length) return;

    const nextSlides = [...(homepage.hero_images || [])];
    const [moved] = nextSlides.splice(index, 1);
    nextSlides.splice(target, 0, moved);
    const ordered = nextSlides.map((item, i) => ({ ...item, sort_order: i }));
    const nextHomepage = {
      ...homepage,
      hero_images: ordered,
      hero_image_url: ordered[0]?.image_url || '',
    };

    setHomepage(nextHomepage);

    try {
      await persistHomepage(nextHomepage);
    } catch (err) {
      setError(err.message || 'Could not reorder hero slides.');
    }
  };

  const toggleSport = key => {
    setExpandedSports(current => ({ ...current, [key]: !current[key] }));
  };

  const toggleLeague = key => {
    setExpandedLeagues(current => ({ ...current, [key]: !current[key] }));
  };

  const startUpload = key => {
    collectionUploadKeyRef.current = key;
    collectionInputRef.current?.click();
  };

  const startAdjust = slot => {
    const entry = getCollectionHeader(homepage.collection_headers, slot.key);
    if (!entry?.image_url) return;
    setCropEditor({
      key: slot.key,
      title: slot.label,
      imageUrl: entry.image_url,
      header_crop: entry.header_crop,
      home_crop: entry.home_crop,
    });
  };

  const saveHeaderCrops = async crops => {
    if (!cropEditor) return;
    setSavingCrop(true);
    setError('');
    const previousHeaders = normalizeCollectionHeaders(homepage.collection_headers);
    const nextHeaders = {
      ...previousHeaders,
      [cropEditor.key]: {
        image_url: cropEditor.imageUrl,
        header_crop: crops.header_crop || { ...DEFAULT_CROP },
        home_crop: crops.home_crop || { ...DEFAULT_CROP },
      },
    };

    setHomepage(current => ({
      ...current,
      collection_headers: nextHeaders,
    }));

    try {
      const saved = await saveCollectionHeaders(nextHeaders);
      setHomepage(current => ({
        ...current,
        collection_headers: saved,
      }));
      setCropEditor(null);
      setSuccess('Crop saved. The homepage and header now show this framing.');
    } catch (err) {
      setHomepage(current => ({
        ...current,
        collection_headers: previousHeaders,
      }));
      setError(err.message || 'Could not save the crop.');
    } finally {
      setSavingCrop(false);
    }
  };

  if (loading) {
    return <p className="admin-empty">Loading settings...</p>;
  }

  const collectionHeaders = normalizeCollectionHeaders(homepage.collection_headers);
  const shopSlot = {
    key: SHOP_HEADER_KEY,
    label: 'Shop — all products',
    hint: 'Header on /shop for the full collection',
  };

  const brandHeaderSlots = (licenseBrands || []).map(brand => ({
    key: brandHeaderKey(brand.slug),
    label: brand.name,
    hint: `Brand page · /brands/${brand.slug}`,
  }));

  return (
    <div>
      <div className="admin-topbar" style={{ marginBottom: 24 }}>
        <div>
          <h1 className="admin-page-title">Settings</h1>
          <p className="admin-page-subtitle">
            Homepage slideshow, collection headers, store information, shipping, and taxes
          </p>
        </div>
        <button
          type="submit"
          form="admin-settings-form"
          className="admin-btn admin-btn-primary"
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save settings'}
        </button>
      </div>

      {error && <p className="admin-error">{error}</p>}
      {success && <p className="admin-success-msg">{success}</p>}

      <form id="admin-settings-form" onSubmit={handleSave}>
        <div className="admin-card">
          <h2 className="admin-card-title">Homepage hero slideshow</h2>
          <p style={{ margin: '0 0 16px', color: '#777', fontSize: 14 }}>
            Upload images for the right side of the homepage. They cycle automatically every few
            seconds. Title and subtitle stay on the left panel.
          </p>

          <div className="admin-grid-2" style={{ marginBottom: 16 }}>
            <div className="admin-field">
              <label className="admin-label">Hero title</label>
              <input
                className="admin-input"
                value={homepage.hero_title || ''}
                onChange={e => setHomepage(h => ({ ...h, hero_title: e.target.value }))}
                placeholder="Laurenti Apparel"
              />
            </div>
            <div className="admin-field">
              <label className="admin-label">Hero subtitle</label>
              <input
                className="admin-input"
                value={homepage.hero_subtitle || ''}
                onChange={e => setHomepage(h => ({ ...h, hero_subtitle: e.target.value }))}
                placeholder="Officially licensed kits and sporting goods."
              />
            </div>
          </div>

          <div
            className={`admin-dropzone${heroDragging ? ' dragging' : ''}`}
            onDragOver={e => {
              e.preventDefault();
              setHeroDragging(true);
            }}
            onDragLeave={() => setHeroDragging(false)}
            onDrop={e => {
              e.preventDefault();
              setHeroDragging(false);
              uploadHero(e.dataTransfer.files);
            }}
          >
            <p className="admin-dropzone-title">
              {uploadingHero ? 'Uploading hero images…' : 'Drag & drop homepage images here'}
            </p>
            <p className="admin-dropzone-hint">
              Multiple images supported. They go live as soon as upload finishes.
            </p>
            <button
              type="button"
              className="admin-btn admin-btn-secondary"
              onClick={() => heroInputRef.current?.click()}
              disabled={uploadingHero}
            >
              {uploadingHero ? 'Uploading…' : 'Browse images'}
            </button>
            <input
              ref={heroInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={e => uploadHero(e.target.files)}
            />
          </div>

          {(homepage.hero_images || []).length === 0 ? (
            <p className="admin-empty" style={{ padding: 16 }}>
              No slideshow images yet. Upload 2+ images to start cycling on the homepage.
            </p>
          ) : (
            <div className="admin-hero-slide-list">
              {(homepage.hero_images || []).map((slide, index) => (
                <div key={`${slide.image_url}-${index}`} className="admin-hero-slide-row">
                  <img src={slide.image_url} alt={`Hero slide ${index + 1}`} />
                  <div className="admin-hero-slide-meta">
                    <strong>Slide {index + 1}</strong>
                    <span>Shows on the right side of the homepage hero</span>
                  </div>
                  <div className="admin-hero-slide-actions">
                    <button
                      type="button"
                      className="admin-btn admin-btn-secondary admin-btn-sm"
                      onClick={() => moveHeroSlide(index, -1)}
                      disabled={index === 0}
                    >
                      Up
                    </button>
                    <button
                      type="button"
                      className="admin-btn admin-btn-secondary admin-btn-sm"
                      onClick={() => moveHeroSlide(index, 1)}
                      disabled={index === homepage.hero_images.length - 1}
                    >
                      Down
                    </button>
                    <button
                      type="button"
                      className="admin-btn admin-btn-danger admin-btn-sm"
                      onClick={() => removeHeroSlide(index)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="admin-card">
          <h2 className="admin-card-title">Collection headers</h2>
          <p style={{ margin: '0 0 16px', color: '#777', fontSize: 14 }}>
            Upload a header for Shop, each sport, league, team, and brand. Sport headers also
            appear on the homepage automatically. After upload, use Move and Scale to choose
            which part of the image shows in each frame.
          </p>

          <input
            ref={collectionInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={e =>
              uploadCollectionHeader(collectionUploadKeyRef.current, e.target.files)
            }
          />

          <div className="admin-hero-slide-list">
            <HeaderSlotRow
              slot={shopSlot}
              entry={getCollectionHeader(collectionHeaders, shopSlot.key)}
              uploading={uploadingCollectionKey === shopSlot.key}
              disabled={Boolean(uploadingCollectionKey) || savingCrop}
              onUpload={startUpload}
              onAdjust={startAdjust}
              onRemove={removeCollectionHeader}
            />

            {headerTree.map(sport => {
              const sportOpen = Boolean(expandedSports[sport.key]);
              const leagueCount = sport.leagues?.length || 0;
              const teamCount =
                (sport.teams?.length || 0) +
                (sport.leagues || []).reduce(
                  (sum, league) => sum + (league.teams?.length || 0),
                  0
                );

              return (
                <div key={sport.key} className="admin-header-group">
                  <div className="admin-header-group-toolbar">
                    <button
                      type="button"
                      className="admin-header-group-toggle"
                      onClick={() => toggleSport(sport.key)}
                      aria-expanded={sportOpen}
                    >
                      {sportOpen ? '▾' : '▸'} {sport.label}
                      <span>
                        {leagueCount
                          ? `${leagueCount} leagues · ${teamCount} teams`
                          : teamCount
                            ? `${teamCount} teams`
                            : 'sport header'}
                      </span>
                    </button>
                  </div>

                  <HeaderSlotRow
                    slot={sport}
                    entry={getCollectionHeader(collectionHeaders, sport.key)}
                    uploading={uploadingCollectionKey === sport.key}
                    disabled={Boolean(uploadingCollectionKey) || savingCrop}
                    onUpload={startUpload}
                    onAdjust={startAdjust}
                    onRemove={removeCollectionHeader}
                  />

                  {sportOpen &&
                    (sport.leagues || []).map(league => {
                      const leagueOpen = Boolean(expandedLeagues[league.key]);
                      return (
                        <div key={league.key} className="admin-header-group admin-header-group--nested">
                          <div className="admin-header-group-toolbar">
                            <button
                              type="button"
                              className="admin-header-group-toggle"
                              onClick={() => toggleLeague(league.key)}
                              aria-expanded={leagueOpen}
                            >
                              {leagueOpen ? '▾' : '▸'} {league.label}
                              <span>
                                {(league.teams || []).length} team
                                {(league.teams || []).length === 1 ? '' : 's'}
                              </span>
                            </button>
                          </div>

                          <HeaderSlotRow
                            slot={league}
                            entry={getCollectionHeader(collectionHeaders, league.key)}
                            uploading={uploadingCollectionKey === league.key}
                            disabled={Boolean(uploadingCollectionKey) || savingCrop}
                            indent={1}
                            onUpload={startUpload}
                            onAdjust={startAdjust}
                            onRemove={removeCollectionHeader}
                          />

                          {leagueOpen &&
                            (league.teams || []).map(team => (
                              <HeaderSlotRow
                                key={team.key}
                                slot={team}
                                entry={getCollectionHeader(collectionHeaders, team.key)}
                                uploading={uploadingCollectionKey === team.key}
                                disabled={Boolean(uploadingCollectionKey) || savingCrop}
                                indent={2}
                                onUpload={startUpload}
                                onAdjust={startAdjust}
                                onRemove={removeCollectionHeader}
                              />
                            ))}
                        </div>
                      );
                    })}

                  {sportOpen &&
                    (sport.teams || []).map(team => (
                      <HeaderSlotRow
                        key={team.key}
                        slot={team}
                        entry={getCollectionHeader(collectionHeaders, team.key)}
                        uploading={uploadingCollectionKey === team.key}
                        disabled={Boolean(uploadingCollectionKey) || savingCrop}
                        indent={1}
                        onUpload={startUpload}
                        onAdjust={startAdjust}
                        onRemove={removeCollectionHeader}
                      />
                    ))}
                </div>
              );
            })}
          </div>

          {brandHeaderSlots.length > 0 && (
            <>
              <h3 className="admin-card-title" style={{ marginTop: 28, fontSize: 16 }}>
                Brand headers
              </h3>
              <p style={{ margin: '0 0 12px', color: '#777', fontSize: 14 }}>
                Add brands under Sports and Brands first, then upload a header for each.
              </p>
              <div className="admin-hero-slide-list">
                {brandHeaderSlots.map(slot => (
                  <HeaderSlotRow
                    key={slot.key}
                    slot={slot}
                    entry={getCollectionHeader(collectionHeaders, slot.key)}
                    uploading={uploadingCollectionKey === slot.key}
                    disabled={Boolean(uploadingCollectionKey) || savingCrop}
                    onUpload={startUpload}
                    onAdjust={startAdjust}
                    onRemove={removeCollectionHeader}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="admin-card">
          <h2 className="admin-card-title">Store information</h2>
          <div className="admin-grid-2">
            <div className="admin-field">
              <label className="admin-label">Store name</label>
              <input
                className="admin-input"
                value={store.store_name || ''}
                onChange={e => setStoreField('store_name', e.target.value)}
              />
            </div>
            <div className="admin-field">
              <label className="admin-label">Email</label>
              <input
                className="admin-input"
                type="email"
                value={store.store_email || ''}
                onChange={e => setStoreField('store_email', e.target.value)}
              />
            </div>
            <div className="admin-field">
              <label className="admin-label">Phone</label>
              <input
                className="admin-input"
                value={store.store_phone || ''}
                onChange={e => setStoreField('store_phone', e.target.value)}
              />
            </div>
            <div className="admin-field">
              <label className="admin-label">Address</label>
              <input
                className="admin-input"
                value={store.store_address || ''}
                onChange={e => setStoreField('store_address', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="admin-card">
          <h2 className="admin-card-title">Currency</h2>
          <div className="admin-grid-2">
            <div className="admin-field">
              <label className="admin-label">Currency code</label>
              <input
                className="admin-input"
                value={store.currency || ''}
                onChange={e => setStoreField('currency', e.target.value)}
              />
            </div>
            <div className="admin-field">
              <label className="admin-label">Currency symbol</label>
              <input
                className="admin-input"
                value={store.currency_symbol || ''}
                onChange={e => setStoreField('currency_symbol', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="admin-card">
          <h2 className="admin-card-title">Shipping</h2>
          <div className="admin-grid-2">
            <div className="admin-field">
              <label className="admin-label">Flat rate</label>
              <input
                className="admin-input"
                type="number"
                min="0"
                step="0.01"
                value={store.shipping_flat_rate}
                onChange={e => setStoreField('shipping_flat_rate', e.target.value)}
              />
            </div>
            <div className="admin-field">
              <label className="admin-label">Free shipping threshold</label>
              <input
                className="admin-input"
                type="number"
                min="0"
                step="0.01"
                value={store.shipping_free_threshold}
                onChange={e => setStoreField('shipping_free_threshold', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="admin-card">
          <h2 className="admin-card-title">Taxes</h2>
          <div className="admin-grid-2">
            <div className="admin-field">
              <label className="admin-label">Tax rate (%)</label>
              <input
                className="admin-input"
                type="number"
                min="0"
                step="0.01"
                value={store.tax_rate}
                onChange={e => setStoreField('tax_rate', e.target.value)}
              />
            </div>
            <div className="admin-field" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label className="admin-checkbox">
                <input
                  type="checkbox"
                  checked={Boolean(store.tax_included)}
                  onChange={e => setStoreField('tax_included', e.target.checked)}
                />
                Prices include tax
              </label>
            </div>
          </div>
        </div>

        <div className="admin-card">
          <h2 className="admin-card-title">Payment methods</h2>
          <div className="admin-field">
            <label className="admin-label">Methods (comma separated)</label>
            <input
              className="admin-input"
              value={
                Array.isArray(store.payment_methods)
                  ? store.payment_methods.join(', ')
                  : store.payment_methods
              }
              onChange={e => setStoreField('payment_methods', e.target.value)}
            />
          </div>
        </div>
      </form>
      {cropEditor ? (
        <HeaderCropEditor
          imageUrl={cropEditor.imageUrl}
          title={cropEditor.title}
          headerKey={cropEditor.key}
          headerCrop={cropEditor.header_crop}
          homeCrop={cropEditor.home_crop}
          saving={savingCrop}
          onCancel={() => setCropEditor(null)}
          onChoose={saveHeaderCrops}
        />
      ) : null}
    </div>
  );
}

export default Settings;
